import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { useClientUser } from '@/hooks';
import { ITEM_SEARCH_DEBOUNCE_MS, ITEM_SEARCH_LIMIT, ITEM_SEARCH_MIN_LENGTH } from './constants';
import { facetOptions, resolveItemSearchMatches, rollupFacetItems } from './utils';
import {
    GET_FORWARD_STOCKING_CARRIER_OPTIONS,
    GET_FORWARD_STOCKING_FACET_ITEMS,
    GET_FORWARD_STOCKING_ITEM_MATCHES,
    GET_FORWARD_STOCKING_SHIPPER_OPTIONS,
    GET_FORWARD_STOCKING_SKUS,
    GET_FORWARD_STOCKING_SKU_ITEMS,
    GET_FORWARD_STOCKING_SUMMARY,
} from './graphql/queries';

const reportQueryError = (error) => captureException(error);

export const useForwardStockingSkus = () => {
    const { user_id } = useClientUser();
    const { data, loading, error } = useQuery(GET_FORWARD_STOCKING_SKUS, {
        variables: { client_id: user_id },
        skip: !user_id,
        fetchPolicy: 'cache-and-network',
        onError: reportQueryError,
    });

    return {
        skus: data?.forward_stocking_sku_summary || [],
        loading: loading || !user_id,
        error,
    };
};

export const useSkuItems = (sku) => {
    const { user_id } = useClientUser();
    const { data, loading, error } = useQuery(GET_FORWARD_STOCKING_SKU_ITEMS, {
        variables: { client_id: user_id, sku },
        skip: !user_id || !sku,
        fetchPolicy: 'cache-and-network',
        onError: reportQueryError,
    });

    return {
        items: data?.forward_stocking_items || [],
        loading,
        error,
    };
};

// `%` and `_` are ILIKE wildcards, so a term containing either would quietly
// widen the match — a lone `%` returning every item in the warehouse.
const escapeLike = (term) => term.replace(/[\\%_]/g, '\\$&');

// Which SKUs and items the search term hits on serial number or line id. Empty
// until the term is worth a round trip; see the constants for why.
export const useItemSearchMatches = (term) => {
    const { user_id } = useClientUser();
    const [debounced, setDebounced] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(term || ''), ITEM_SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [term]);

    const trimmed = debounced.trim();
    const enabled = trimmed.length >= ITEM_SEARCH_MIN_LENGTH;

    const { data, loading, error } = useQuery(GET_FORWARD_STOCKING_ITEM_MATCHES, {
        variables: { client_id: user_id, term: `%${escapeLike(trimmed)}%`, limit: ITEM_SEARCH_LIMIT },
        skip: !user_id || !enabled,
        fetchPolicy: 'cache-and-network',
        onError: reportQueryError,
    });

    return useMemo(() => {
        const resolved = resolveItemSearchMatches({
            term,
            debounced,
            matches: data?.forward_stocking_items,
            minLength: ITEM_SEARCH_MIN_LENGTH,
        });
        return {
            matchedSkus: resolved.matchedSkus,
            matchedItemIds: resolved.matchedItemIds,
            loading: resolved.enabled && (loading || resolved.stale),
            error,
        };
    }, [data, debounced, error, loading, term]);
};

export const useForwardStockingSummary = () => {
    const { user_id } = useClientUser();
    const { data, loading, error } = useQuery(GET_FORWARD_STOCKING_SUMMARY, {
        variables: { client_id: user_id },
        skip: !user_id,
        fetchPolicy: 'cache-and-network',
        onError: reportQueryError,
    });

    // The view has one row per client, and none at all for a client with no
    // forward stocked inventory.
    return {
        summary: data?.client_forward_stocking_summary?.[0] || null,
        loading: loading || !user_id,
        error,
    };
};

// The parties the viewer can narrow by, name-ordered. One row per party rather
// than per item, so this is cheap enough to load with the page.
export const useFacetOptions = (facet) => {
    const { user_id } = useClientUser();
    const { data, loading, error } = useQuery(
        facet?.key === 'shipper' ? GET_FORWARD_STOCKING_SHIPPER_OPTIONS : GET_FORWARD_STOCKING_CARRIER_OPTIONS,
        {
            variables: { client_id: user_id },
            skip: !user_id || !facet,
            fetchPolicy: 'cache-and-network',
            onError: reportQueryError,
        }
    );

    return useMemo(
        () => ({ options: facetOptions(data?.forward_stocking_items, facet), loading, error }),
        [data, facet, loading, error]
    );
};

// The selection's per-SKU counts and KPI summary. Null means nothing is
// selected and the page should use the roll-up views as-is.
export const useFacetRollup = (facet, selectedIds) => {
    const { user_id } = useClientUser();
    const active = Boolean(facet && selectedIds?.length);

    // previousData keeps the last answer on screen while a new selection is in
    // flight, so changing the filter does not blank the table for a frame.
    const { data, previousData, loading, error } = useQuery(GET_FORWARD_STOCKING_FACET_ITEMS, {
        variables: {
            where: { client_id: { _eq: user_id }, ...(facet ? { [facet.idField]: { _in: selectedIds } } : {}) },
        },
        skip: !user_id || !active,
        fetchPolicy: 'cache-and-network',
        onError: reportQueryError,
    });

    const rows = data?.forward_stocking_items || previousData?.forward_stocking_items;

    return useMemo(
        () => ({
            rollup: active && rows ? rollupFacetItems(rows) : null,
            loading: active && loading,
            error,
        }),
        [active, rows, loading, error]
    );
};
